import React from 'react'
import { Link, useParams } from 'react-router-dom'
import { FileText, SquarePen, Users } from 'lucide-react'
import Card from '../ui/Card'
import Button from '../ui/Button'
import Badge from '../ui/Badge'
import IconLinkButton from '../ui/IconLinkButton'
import { useProjects } from '../../contexts/ProjectsContext'
import PageLoader from '../ui/PageLoader'
import PageHeader from '../layout/PageHeader'
import ProjectPageTitle from '../layout/ProjectPageTitle'
import { formatClientLocationSubtitle } from '../../utils/projectSubtitle'
import { useData360ProjectPipeSets } from '../../hooks/useData360ProjectPipeSets'
import { DATA_360_DISPLAY_NAME } from '../../utils/data360DisplayName'
import { useProjectMechanicalReports } from '../../hooks/useProjectMechanicalReports'
import { usePhantomProjectRedirect } from '../../hooks/usePhantomProjectRedirect'
import type { PipeSizeSet } from '../../types'

function humanizeSubType(subType: string): string {
  return subType
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

function pipeTypePillLabel(pipe: PipeSizeSet): string | null {
  if (!pipe.data360ProjectCategory) return null
  const base =
    pipe.data360ProjectCategory === 'pipeline' ? 'Pipeline' : 'Fabrication'
  // if (pipe.data360ProjectCategory === 'fabrication' && pipe.data360ProjectSubType) {
  //   return `${base} · ${humanizeSubType(pipe.data360ProjectSubType)}`
  // }
  return base
}

function pipeTypePillTone(pipe: PipeSizeSet): 'primary' | 'success' {
  return pipe.data360ProjectCategory === 'fabrication' ? 'success' : 'primary'
}

export default function ProjectNextSteps() {
  const { id } = useParams()
  const { getProject, projectsLoading } = useProjects()
  const resolvingPhantom = usePhantomProjectRedirect()
  const project = id ? getProject(id) : undefined
  const { pipeSets, pipesLoading } = useData360ProjectPipeSets(project)
  const showEmptyState = !pipesLoading && pipeSets.length === 0
  const { hasMechanicalReports, loading: mechReportsLoading } = useProjectMechanicalReports(
    project?.id,
    showEmptyState
  )
  const showBlockingPipeLoader = pipesLoading && pipeSets.length === 0
  const showSyncingPill = pipesLoading && pipeSets.length > 0

  if (resolvingPhantom || (id && projectsLoading)) {
    return <PageLoader label="Loading project…" />
  }

  if (!project) {
    return (
      <Card title="Project not found">
        <p className="text-sm text-gray-500">Return to the Project Hub to select a project.</p>
        <div className="mt-4">
          <Link to="/resources">
            <Button>Go to Project Hub</Button>
          </Link>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        backTo={{ to: '/resources', label: 'Back to Project Hub' }}
        title={project.name}
        subtitle={
          <>
            {formatClientLocationSubtitle(project) ? (
              <span>{formatClientLocationSubtitle(project)}</span>
            ) : null}
            <Badge tone="primary">{project.applications?.join(', ') || project.application}</Badge>
            <Badge tone="gray">{pipeSets.length} pipe size sets</Badge>
            {project.data360ProjectId ? (
              <Badge tone="gray">{DATA_360_DISPLAY_NAME} #{project.data360ProjectId}</Badge>
            ) : (
              <Badge tone="gray">Legacy</Badge>
            )}
            {showSyncingPill ? <Badge tone="warning">Syncing pipes…</Badge> : null}
          </>
        }
        actions={
          <>
            <IconLinkButton
              to={`/projects/${project.id}/team-members`}
              label="Team members"
              icon={Users}
            />
            <IconLinkButton
              to={`/projects/${project.id}/edit`}
              label="Pipe sets"
              icon={SquarePen}
            />
            <IconLinkButton
              to={`/projects/${project.id}/mechanical-tests`}
              label="Mechanical test reports"
              icon={FileText}
            />
          </>
        }
      />

      {showBlockingPipeLoader ? (
        <PageLoader label={`Syncing pipes from ${DATA_360_DISPLAY_NAME}…`} compact />
      ) : null}

      {showEmptyState ? (
        mechReportsLoading ? (
          <PageLoader label="Checking mechanical reports…" compact />
        ) : (
          <Card title="No Pipe Sets Yet">
            <p className="text-sm text-gray-500">
              {hasMechanicalReports
                ? 'No Pipe Sets have been created for this project yet. Create your first Pipe Set to begin working in WPS Studio.'
                : 'No Pipe Sets have been created for this project yet. Create your first Pipe Set to begin working in WPS Studio.'}
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              {hasMechanicalReports ? (
                <>
                  <Link to={`/projects/${project.id}/mechanical-tests`}>
                    <Button> 
                      <FileText className="mr-2 h-4 w-4" />
                      Mechanical Test Reports</Button>
                  </Link>
                  <Link to={`/projects/${project.id}/edit`}>
                    <Button variant="secondary">
                      
                      <SquarePen className="mr-2 h-4 w-4" />
                      Create Pipe Set</Button>
                  </Link>
                </>
              ) : (
                <Link to={`/projects/${project.id}/edit`}>
                  <Button>
                    
                    <SquarePen className="mr-2 h-4 w-4" />
                    Create Pipe Set</Button>
                </Link>
              )}
            </div>
          </Card>
        )
      ) : pipeSets.length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {pipeSets.map((pipe, index) => {
            const typePill = pipeTypePillLabel(pipe)
            const importedWpsNumber = (pipe.wpsNumber || '').trim()
            const dimsLabel = `${pipe.nominalOd} ${pipe.nominalOdUnit} × ${pipe.wallThickness} ${pipe.wallThicknessUnit}`
            return (
            <div
              key={pipe.id}
              className="group relative flex h-full flex-col rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition hover:border-gray-300 hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  {importedWpsNumber ? (
                    <>
                      <p className="text-base font-semibold text-gray-900 truncate">
                        {importedWpsNumber}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">{dimsLabel}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-xs text-gray-500">Pipe Set {index + 1}</p>
                      <p className="mt-1 text-base font-semibold text-gray-900">
                        {dimsLabel}
                      </p>
                    </>
                  )}
                </div>
                <div className="flex shrink-0 flex-nowrap items-center justify-end gap-1.5">
                  {typePill ? <Badge tone={pipeTypePillTone(pipe)}>{typePill}</Badge> : null}
                  {!importedWpsNumber ? (
                    <span className="rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-semibold text-primary-700">
                      {`WPS ${String(index + 1).padStart(2, '0')}`}
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="mt-auto flex items-center justify-end pt-3">
                <Link to={`/projects/${project.id}/next/${pipe.id}`}>
                  <Button className="h-8 px-2.5 text-xs">View details</Button>
                </Link>
              </div>
            </div>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
